import React from 'react';
import { Typography, Paper, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HistoryIcon from '@mui/icons-material/History';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const BillingAndPayment = () => {
  const billingFeatures = [
    {
      icon: <PaymentIcon />,
      title: "Manage Payment Methods",
      description: "Add, edit, or remove credit cards, bank accounts, or other payment methods for your AI Talker subscription."
    },
    {
      icon: <ReceiptIcon />,
      title: "View and Download Invoices",
      description: "Access your billing history and download invoices for your records or accounting purposes."
    },
    {
      icon: <AccountBalanceIcon />,
      title: "Subscription Management",
      description: "View your current plan, upgrade or downgrade your subscription, and manage add-ons as needed."
    },
    {
      icon: <HistoryIcon />,
      title: "Usage Reports",
      description: "Monitor your AI Talker usage and associated costs over time to optimize your subscription."
    },
    {
      icon: <NotificationsActiveIcon />,
      title: "Billing Notifications",
      description: "Set up alerts for payment due dates, usage thresholds, or other billing-related events."
    }
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>Billing and Payment</Typography>
      <Typography paragraph>
        The Billing and Payment section allows you to manage all aspects of your AI Talker subscription, 
        including payment methods, invoices, and usage tracking. Stay on top of your account's financial 
        details and ensure uninterrupted access to AI Talker services.
      </Typography>

      <List>
        {billingFeatures.map((feature, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              {feature.icon}
            </ListItemIcon>
            <ListItemText 
              primary={feature.title}
              secondary={feature.description}
            />
          </ListItem>
        ))}
      </List>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Understanding Your Billing:</Typography>
      <Typography paragraph>
        1. <strong>Subscription Plans:</strong> AI Talker offers various subscription tiers to suit different business needs. 
        Review the features and limitations of each plan to choose the best fit for your organization.
      </Typography>
      <Typography paragraph>
        2. <strong>Billing Cycle:</strong> Subscriptions are typically billed monthly or annually. Annual subscriptions often 
        come with a discount compared to monthly billing.
      </Typography>
      <Typography paragraph>
        3. <strong>Usage-Based Billing:</strong> Some features or high-volume usage may incur additional charges. 
        Monitor your usage reports to stay within your plan limits or anticipate any extra costs.
      </Typography>
      <Typography paragraph>
        4. <strong>Payment Processing:</strong> Payments are automatically processed on your billing date using your 
        designated payment method. Ensure your payment information is up-to-date to avoid service interruptions.
      </Typography>
      <Typography paragraph>
        5. <strong>Invoices and Receipts:</strong> Detailed invoices are generated for each billing cycle and can be 
        downloaded for your records. These include itemized charges and any applicable taxes.
      </Typography>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Managing Your Subscription:</Typography>
      <Typography paragraph>
        - Regularly review your subscription to ensure it meets your current needs. You can upgrade or downgrade at any time.
      </Typography>
      <Typography paragraph>
        - Set up usage alerts to be notified when you're approaching your plan limits, helping you avoid unexpected charges.
      </Typography>
      <Typography paragraph>
        - Keep your payment methods current to ensure uninterrupted service. We recommend having a backup payment method on file.
      </Typography>
      <Typography paragraph>
        - Review your invoices each billing cycle to understand your charges and track your AI Talker expenses over time.
      </Typography>

      <Typography paragraph style={{ marginTop: '20px' }}>
        Our billing system is designed to be transparent and flexible, allowing you to easily manage your 
        AI Talker subscription. If you have any questions about your bill, need to update your payment information, 
        or want to discuss custom pricing options for large-scale implementations, our billing support team is 
        here to assist you.
      </Typography>
      <Typography paragraph>
        Remember, maintaining an active subscription ensures you have continuous access to AI Talker's powerful 
        features and our dedicated support team. If you ever experience any billing issues or have questions about 
        your charges, don't hesitate to reach out to our customer support for prompt assistance.
      </Typography>
    </Paper>
  );
};

export default BillingAndPayment;
